import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaPlus, FaFilePdf } from "react-icons/fa";
import { toast } from "react-toastify";

import { PageWrapper } from "../components/admin";
import { Button } from "../components/form";
import { BaseModal } from "../components/popups";
import { FullScreenLoader } from "../components/common";

import { getCustomPdfs, createCustomPdf, deleteCustomPdf } from "../service";

export default function CustomPdfs() {
  const [pdfs, setPdfs] = useState([]);
  const [deleteItem, setDeleteItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const { orgId } = useParams();
  const navigate = useNavigate();

  const fetchCustomPdfs = async () => {
    try {
      setLoading("Loading custom PDFs...");
      const { data } = await getCustomPdfs(orgId);
      setPdfs(data);
    } catch (error) {
      console.error("Fetch custom PDFs error", error);
      toast.error("Failed to fetch custom PDFs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePdf = async () => {
    if (!newTitle.trim()) {
      toast.error("Please enter a title");
      return;
    }

    try {
      setLoading("Creating custom PDF...");
      const { data } = await createCustomPdf({ title: newTitle, json: {} }, orgId);
      toast.success("Custom PDF created successfully");
      setShowCreateModal(false);
      setNewTitle("");
      // Navigate to editor
      navigate(`/dashboard/organisation/custom-pdfs/${orgId}/${data._id}`);
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Failed to create custom PDF");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPdf = (pdf) => {
    navigate(`/dashboard/organisation/custom-pdfs/${orgId}/${pdf._id}`);
  };

  const confirmDeletePdf = async (pdf) => {
    try {
      setLoading("Deleting custom PDF...");
      await deleteCustomPdf(orgId, pdf._id);
      setDeleteItem(null);
      toast.success("Custom PDF deleted successfully");
      await fetchCustomPdfs();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Failed to delete custom PDF");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  useEffect(() => {
    if (!orgId) return;
    fetchCustomPdfs();
  }, [orgId]);

  return (
    <div>
      <PageWrapper pageHeading={"Custom PDFs"}>
        {pdfs.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            No custom PDFs created yet. Click the button below to create one.
          </div>
        )}

        {pdfs.map((pdf) => (
          <div key={pdf._id} className="bg-white shadow-sm mb-4 rounded">
            <div className="flex flex-row justify-between items-center px-4 py-3 hover:bg-gray-50">
              <div className="flex items-center gap-4">
                <FaFilePdf className="text-red-500 text-xl" />
                <div>
                  <h3 className="font-semibold text-gray-800">{pdf.title || "Untitled PDF"}</h3>
                  <p className="text-sm text-gray-500">Last updated: {formatDate(pdf.updatedAt)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="p-2 hover:bg-blue-100 rounded"
                  onClick={() => handleEditPdf(pdf)}
                  title="Edit PDF">
                  <FaEdit className="text-blue-600" />
                </button>
                <button
                  className="p-2 hover:bg-red-100 rounded"
                  onClick={() => setDeleteItem(pdf)}
                  title="Delete PDF">
                  <FaTrash className="text-red-600" />
                </button>
              </div>
            </div>
          </div>
        ))}

        <Button.ButtonOutlined
          className="my-3 w-full font-bold flex items-center justify-center gap-2"
          handler={() => setShowCreateModal(true)}>
          <FaPlus className="w-4 h-4" />
          Create New Custom PDF
        </Button.ButtonOutlined>

        {/* Create Modal */}
        <BaseModal
          open={showCreateModal}
          setOpen={setShowCreateModal}
          title="Create New Custom PDF"
          customButtonTitle="Create"
          customButtonAction={handleCreatePdf}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PDF Title *</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Enter PDF title"
              onKeyPress={(e) => e.key === "Enter" && handleCreatePdf()}
            />
          </div>
        </BaseModal>

        {/* Delete Confirmation Modal */}
        <BaseModal
          open={deleteItem ? true : false}
          setOpen={setDeleteItem}
          title="Delete Custom PDF">
          {deleteItem && (
            <div>
              <p className="text-base text-black mb-4">
                Are you sure you want to delete "{deleteItem.title || "Untitled PDF"}"?
              </p>
              <p className="text-sm text-gray-600 mb-4">This action cannot be undone.</p>
              <Button.ButtonOutlined
                className="border-red-500 flex items-center justify-center w-full text-red-500 gap-2"
                handler={() => confirmDeletePdf(deleteItem)}>
                <FaTrash className="w-4 h-4" />
                Delete PDF
              </Button.ButtonOutlined>
            </div>
          )}
        </BaseModal>

        {loading && <FullScreenLoader text={loading} />}
      </PageWrapper>
    </div>
  );
}
